/**
 * ChartMind's steps, their labels, and what "Next" means on each of them.
 *
 * ⚠️ THIS IS ONE SOURCE OF TRUTH ON PURPOSE. Desktop under-tabs nav (ChartMindStepNav) and the
 * paired phone remote both read getStepActions. Two copies would eventually disagree, and a
 * phone offering a Next the desktop would refuse is worse than a phone with no Next at all.
 */

export const STEPS = {
  RECORD: "record",
  DIAGNOSIS: "diagnosis",
  // Focused exam (Exam / Exam 2) after differential; before Pearls.
  EXAM: "exam",
  // Teaching pearls for the chosen diagnosis. Hidden where the chartmind-pearls prompt is not set up.
  PEARLS: "pearls",
  DIAGNOSTIC_PLAN: "diagnostic_plan",
  CONFIRM_DIAGNOSIS: "confirm_diagnosis",
  TREATMENT: "treatment",
  // Inpatient: mid-flow for admission/progress, first step for the Events note type.
  EVENTS: "events",
  DISCHARGE: "discharge",
  CHART: "chart",
};

/** Step order for progressive unlocking. */
export const STEP_ORDER = [
  STEPS.RECORD,
  STEPS.DIAGNOSIS,
  STEPS.EXAM,
  STEPS.PEARLS,
  STEPS.DIAGNOSTIC_PLAN,
  STEPS.CONFIRM_DIAGNOSIS,
  STEPS.TREATMENT,
  STEPS.EVENTS,
  STEPS.DISCHARGE,
  STEPS.CHART,
];

/**
 * Events note type: Event is the first tab (peer to Narrative on the other note types), then
 * Differential → Exam → Pearls → Diagnostics → Treatments → Chart.
 */
export const EVENTS_NOTE_STEP_ORDER = [
  STEPS.EVENTS,
  STEPS.DIAGNOSIS,
  STEPS.EXAM,
  STEPS.PEARLS,
  STEPS.DIAGNOSTIC_PLAN,
  STEPS.TREATMENT,
  STEPS.CHART,
];

/**
 * What each step is called, for the stepper and for the phone's "on your computer" line.
 *
 * Exam / Diagnostics / Treatments are separate tabs everywhere. Confirm stays outpatient-only
 * and may still be hidden under CM0427; Treatment is no longer relabeled "Plan" when Diagnostics
 * is visible.
 */
export const getStepLabels = (cm0427Mode = false, inpatient = false, eventsNote = false) => ({
  [STEPS.RECORD]: inpatient ? "Narrative" : "Recording",
  [STEPS.DIAGNOSIS]: eventsNote ? "Differential" : "Diagnosis",
  [STEPS.EXAM]: "Exam",
  [STEPS.PEARLS]: "Pearls",
  [STEPS.DIAGNOSTIC_PLAN]: "Diagnostics",
  [STEPS.CONFIRM_DIAGNOSIS]: "Confirm",
  [STEPS.TREATMENT]: eventsNote ? "Treatments" : inpatient ? "Daily Plan" : "Treatment",
  [STEPS.EVENTS]: eventsNote ? "Event" : "Events",
  [STEPS.DISCHARGE]: "Discharge",
  [STEPS.CHART]: "Chart",
});

/**
 * The steps the stepper shows.
 *
 * Events: Event, Differential, Exam, Pearls, Diagnostics, Treatments, Chart.
 * Admission/Progress: Narrative … Exam, Pearls, Diagnostics, Daily Plan, Discharge, Chart
 *   (no mid-flow Events tab — Events is its own note type).
 * Outpatient: Recording … Exam, Pearls, Diagnostics, (Confirm unless CM0427), Treatment, Discharge, Chart.
 * Pearls drops out where its prompt is not set up.
 */
export const getVisibleStepOrder = (
  cm0427Mode = false,
  inpatient = false,
  pearlsAvailable = true,
  eventsNote = false,
) => {
  if (eventsNote) {
    return EVENTS_NOTE_STEP_ORDER.filter((step) => {
      if (step === STEPS.PEARLS) return pearlsAvailable;
      return true;
    });
  }
  return STEP_ORDER.filter((step) => {
    // Mid-flow Events tab is gone for Admission/Progress; only the Events note type uses it.
    if (step === STEPS.EVENTS) return false;
    if (step === STEPS.PEARLS) return pearlsAvailable;
    // Confirm remains outpatient-only and stays gated by CM0427.
    if (step === STEPS.CONFIRM_DIAGNOSIS) {
      return !cm0427Mode && !inpatient;
    }
    return true;
  });
};

/**
 * The footer's button configuration for a step, plus why Next is unavailable when it is.
 *
 * ⚠️ nextBlockedReason IS FOR THE PHONE, and it exists because a disabled button is only honest
 * when you can see the screen that explains it. On the desktop the greyed-out Next sits under
 * the diagnoses you have not picked yet, so the reason is on screen. On a phone in a corridor
 * there is nothing around it, and "Next is greyed out" with no cause reads as a bug. It says
 * what to do rather than what is wrong, and it names the computer, because that is where the
 * clinician has to do it.
 *
 * @param {Object} params
 * @param {string} params.currentStep
 * @param {string} params.transcript
 * @param {number} [params.eventsCount] - named events on the Events note type's first step
 * @param {number} params.selectedDiagnosesCount
 * @param {number} params.selectedConfirmedDiagnosesCount
 * @param {boolean} params.clinicianImpressionRecorded  whether the clinician has given their
 *   own diagnosis yet — until they have, the Diagnosis step shows the gate and no differential
 * @param {boolean} params.cm0427Mode
 * @param {boolean} params.inpatient  Admission/Progress (Daily Plan labels; no mid-flow Events)
 * @param {boolean} params.eventsNote  Events note type: Event-first short flow
 * @param {boolean} params.pearlsAvailable  false skips the Pearls step
 * @param {Object} params.handlers  the onContinueTo* callbacks, by name
 */
export const getStepActions = ({
  currentStep,
  transcript,
  eventsCount = 0,
  selectedDiagnosesCount = 0,
  selectedConfirmedDiagnosesCount = 0,
  clinicianImpressionRecorded = true,
  cm0427Mode = false,
  inpatient = false,
  eventsNote = false,
  pearlsAvailable = true,
  handlers = {},
}) => {
  // Confirm is the only step still skipped for inpatient / Events / CM0427. Exam + Diagnostics
  // are always in the trio; shortFlow only means "no Confirm, gate later steps on selected Dx".
  const shortFlow = cm0427Mode || inpatient || eventsNote;
  const {
    onContinueToDiagnosis,
    onContinueToExam,
    onContinueToPearls,
    onContinueToPlan,
    onContinueToConfirm,
    onContinueToTreatment,
    onContinueToEvents,
    onContinueToDischarge,
    onGenerateChart,
    onSkipToChart,
  } = handlers;

  // [BETA-CM0427] With Confirm hidden, the later steps gate on selected (preliminary)
  // diagnoses instead of confirmed ones — there is no Confirm step to produce them.
  const diagnosesGateCount = shortFlow
    ? selectedDiagnosesCount
    : selectedConfirmedDiagnosesCount;
  const diagnosesGateReason = shortFlow
    ? "Please choose diagnoses on your computer first."
    : "Please confirm diagnoses on your computer first.";

  switch (currentStep) {
    case STEPS.RECORD: {
      // The stepper already allows jumping straight to Chart, but users were not finding it.
      // Surface it as a secondary action here. Same transcript gate as Next: the note is built
      // from the transcript alone, which generateNote supports (diagnoses are optional context).
      const noUsableTranscript = !transcript || transcript.length < 50;
      return {
        showBack: false,
        showNext: true,
        nextLabel: "Choose Diagnoses",
        nextDisabled: noUsableTranscript,
        nextBlockedReason: noUsableTranscript
          ? "Please record the encounter first."
          : "",
        onNext: onContinueToDiagnosis,
        secondaryLabel: "Skip to Chart",
        secondaryDisabled: noUsableTranscript,
        onSecondary: onSkipToChart,
      };
    }

    case STEPS.DIAGNOSIS: {
      const diagnosisBlocked =
        selectedDiagnosesCount > 0
          ? ""
          : eventsNote
            ? "Please keep at least one diagnosis on your computer first."
            : clinicianImpressionRecorded
              ? "Please choose diagnoses on your computer first."
              : "Please enter your own diagnosis on your computer first.";
      const diagnosisDisabled = selectedDiagnosesCount === 0;
      return {
        showBack: true,
        showNext: true,
        nextLabel: "Review Exam",
        nextDisabled: diagnosisDisabled,
        nextBlockedReason: diagnosisBlocked,
        onNext: onContinueToExam,
      };
    }

    case STEPS.EXAM: {
      // When Pearls exists: outlined Pearls (optional) + filled Diagnostics (skip Pearls).
      const examBlocked = selectedDiagnosesCount === 0;
      const examBlockedReason = examBlocked
        ? "Please choose diagnoses on your computer first."
        : "";
      if (pearlsAvailable) {
        return {
          showBack: true,
          showNext: true,
          nextLabel: "Diagnostics",
          nextDisabled: examBlocked,
          nextBlockedReason: examBlockedReason,
          onNext: onContinueToPlan,
          secondaryLabel: "Pearls",
          secondaryDisabled: examBlocked,
          onSecondary: onContinueToPearls,
        };
      }
      return {
        showBack: true,
        showNext: true,
        nextLabel: "Diagnostics",
        nextDisabled: examBlocked,
        nextBlockedReason: examBlockedReason,
        onNext: onContinueToPlan,
      };
    }

    case STEPS.PEARLS:
      return {
        showBack: true,
        showNext: true,
        nextLabel: "Review Diagnostics",
        nextDisabled: selectedDiagnosesCount === 0,
        nextBlockedReason:
          selectedDiagnosesCount === 0 ? "Please choose diagnoses on your computer first." : "",
        onNext: onContinueToPlan,
      };

    case STEPS.DIAGNOSTIC_PLAN: {
      const afterDiagnostics = shortFlow ? onContinueToTreatment : onContinueToConfirm;
      const nextLabel = shortFlow
        ? eventsNote
          ? "Review Treatments"
          : inpatient
            ? "Review Daily Plan"
            : "Review Treatments"
        : "Confirm Diagnosis";
      return {
        showBack: true,
        showNext: true,
        nextLabel,
        nextDisabled: selectedDiagnosesCount === 0,
        nextBlockedReason:
          selectedDiagnosesCount === 0
            ? "Please choose diagnoses on your computer first."
            : "",
        onNext: afterDiagnostics,
      };
    }

    case STEPS.CONFIRM_DIAGNOSIS:
      return {
        showBack: true,
        showNext: true,
        nextLabel: "Review Treatments",
        nextDisabled: selectedConfirmedDiagnosesCount === 0,
        nextBlockedReason:
          selectedConfirmedDiagnosesCount === 0
            ? "Please confirm diagnoses on your computer first."
            : "",
        onNext: onContinueToTreatment,
      };

    case STEPS.TREATMENT:
      return {
        showBack: true,
        showNext: true,
        nextLabel: eventsNote ? "Generate Note" : "Review Discharge",
        nextDisabled: diagnosesGateCount === 0,
        nextBlockedReason: diagnosesGateCount === 0 ? diagnosesGateReason : "",
        onNext: eventsNote ? onGenerateChart : onContinueToDischarge,
      };

    // Events note type only: Event is the first tab; needs at least one named event before
    // Diagnosis (or Skip to Chart). Add Event also advances. Admission/Progress no longer
    // expose a mid-flow Events step.
    case STEPS.EVENTS: {
      if (eventsNote) {
        const noEvents = eventsCount < 1;
        return {
          showBack: false,
          showNext: true,
          nextLabel: "Choose Diagnoses",
          nextDisabled: noEvents,
          nextBlockedReason: noEvents ? "Please add an event on your computer first." : "",
          onNext: onContinueToDiagnosis,
          secondaryLabel: "Skip to Chart",
          secondaryDisabled: noEvents,
          onSecondary: onSkipToChart,
        };
      }
      return {
        showBack: true,
        showNext: true,
        nextLabel: "Review Discharge",
        nextDisabled: false,
        nextBlockedReason: "",
        onNext: onContinueToDischarge,
      };
    }

    case STEPS.DISCHARGE:
      return {
        showBack: true,
        showNext: true,
        nextLabel: "Generate Note",
        nextDisabled: diagnosesGateCount === 0,
        nextBlockedReason: diagnosesGateCount === 0 ? diagnosesGateReason : "",
        onNext: onGenerateChart,
      };

    case STEPS.CHART:
      // The end of the flow. Nothing to advance to, which is why the phone shows no button
      // here rather than a disabled one: there is no action being withheld.
      return { showBack: true, showNext: false };

    default:
      return { showBack: false, showNext: false };
  }
};
